import * as React from "react"
import { ObjectKind } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgCornerDownLeft } from "@digits-shared/components/SVGIcons/line/CornerDownLeft.svg"
import { SvgPlus } from "@digits-shared/components/SVGIcons/line/Plus.svg"
import colorHelper from "@digits-shared/helpers/colorHelper"
import colors from "@digits-shared/themes/colors"
import fonts, { BodyText, LabelText } from "@digits-shared/themes/typography"
import styled from "styled-components"
import {
  newCategoryEntityResultWithId,
  newPartyEntityResultWithId,
  type OnSelectedCallback,
} from "src/shared/components/Typeahead/sharedTypeahead"

/*
 STYLES
*/

const AddNewContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-top: 1px solid ${colors.translucentBlack10};
  height: 52px;

  &:hover,
  &.selected {
    cursor: pointer;
    background: ${colors.white};
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Term = styled(BodyText)`
  b {
    font-weight: ${fonts.weight.heavy};
  }
`

const AddHelp = styled(LabelText)`
  color: ${colors.translucentSecondary80};
  display: flex;
  align-items: center;
`

const Enter = styled.span`
  color: ${colors.secondary};
  font-weight: ${fonts.weight.heavy};
  border: 1px solid ${colors.translucentSecondary40};
  border-radius: 2px;
  padding: 1px 4px;
  display: inline-flex;
  align-items: center;
  margin: 2px 4px 0;
  gap: 4px;
`

const EnterIcon = styled(SvgCornerDownLeft)`
  ${svgPathStyles(colors.secondary)};
  height: 10px;
  width: 10px;
`

const PlusIcon = styled(SvgPlus)`
  width: 16px;
  height: 16px;
  ${svgPathStyles(colors.accentBlue, 2)};
`

const NewIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  // Other Icons are 35px
  margin: 0 5.5px;
  width: 24px;
  height: 24px;
  background: ${colorHelper.hexToRgba(colors.accentBlue, 0.1)};
  border-radius: 50%;

  &.vibrant {
    background: ${colors.accentGreen};
    svg {
      ${svgPathStyles(colors.secondaryDark, 2)};
    }
  }
`

/*
 COMPONENTS
*/

export const AddNewResult: React.FC<{
  searchTerm: string
  onResultSelected: OnSelectedCallback
  kind: ObjectKind
  vibrant?: boolean
  selected?: boolean
}> = ({ searchTerm, onResultSelected, kind, vibrant, selected }) => {
  const addNewResult = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()

      switch (kind) {
        case ObjectKind.Category:
          onResultSelected(newCategoryEntityResultWithId(searchTerm.trim()))
          break
        case ObjectKind.Party:
          onResultSelected(newPartyEntityResultWithId(searchTerm.trim()))
          break
        default:
          break
      }
    },
    [kind, onResultSelected, searchTerm]
  )

  return (
    <AddNewContainer onMouseDown={addNewResult} className={selected ? "selected" : ""}>
      <NewResultIcon vibrant={vibrant} />
      <TextContainer>
        <Term>
          Add <b>{`"${searchTerm}"`}</b>
        </Term>

        {!selected && (
          <AddHelp>
            Shift +
            <Enter>
              <EnterIcon />
            </Enter>
          </AddHelp>
        )}
      </TextContainer>
    </AddNewContainer>
  )
}

export const NewResultIcon: React.FC<{ vibrant?: boolean }> = ({ vibrant }) => (
  <NewIcon className={vibrant ? "vibrant" : ""}>
    <PlusIcon />
  </NewIcon>
)
